/**
 * Modern video ads kill switches (IMA/VAST/VMAP).
 * OFF by default — production players must not activate without Product approval.
 *
 * @see docs/TPG_MODERN_VIDEO_ADVERTISING.md
 */

/** Official IMA HTML5 SDK loader (not injected unless modern ads explicitly enabled). */
export var IMA_SDK_DEFAULT_URL =
  "https://imasdk.googleapis.com/js/sdkloader/ima3.js";

/**
 * Resolve modern-IMA enablement. Defaults hard-off.
 * Opt-in only via explicit player config / harness — never ambient truthy ads flags.
 *
 * @param {object} [options]
 * @param {object} [options.features] player feature flags
 * @param {object} [options.avod] BE avodConfig / ads/avod payload
 * @param {boolean} [options.forceLab] lab harness opt-in
 * @returns {{ enabled: boolean, liveEnabled: boolean, reason: string, imaSdkUrl: string }}
 */
export function resolveModernVideoAdsFlags(options) {
  options = options || {};
  var features = options.features || {};
  var avod = options.avod || {};
  var modern = avod.modern || {};

  var configEnabled = modern.enabled === true;
  var featureEnabled = features.modernImaAds === true;
  var lab = options.forceLab === true;

  var liveConfig = modern.live_ads_enabled === true;
  var liveFeature = features.liveImaAds === true;

  // Production activation requires BOTH FE feature and BE modern.enabled.
  // Lab may forceLab for harness-only smoke tests without flipping BE.
  var enabled = lab || (configEnabled && featureEnabled);
  var liveEnabled = enabled && (lab ? false : liveConfig && liveFeature);

  var reason = "disabled_by_default";
  if (lab) {
    reason = "lab_harness_only";
  } else if (enabled) {
    reason = "explicit_opt_in";
  } else if (configEnabled && !featureEnabled) {
    reason = "awaiting_player_feature_flag";
  } else if (featureEnabled && !configEnabled) {
    reason = "awaiting_backend_modern_enabled";
  }

  return {
    enabled: !!enabled,
    liveEnabled: !!liveEnabled,
    reason: reason,
    imaSdkUrl:
      (modern.ima_sdk_url && String(modern.ima_sdk_url)) || IMA_SDK_DEFAULT_URL,
  };
}

/**
 * @returns {boolean}
 */
export function isModernVideoAdsEnabled(options) {
  return resolveModernVideoAdsFlags(options).enabled;
}

/**
 * Live IMA remains disabled until Product approval.
 * @returns {boolean}
 */
export function isLiveImaAdsEnabled(options) {
  return resolveModernVideoAdsFlags(options).liveEnabled;
}

export default {
  IMA_SDK_DEFAULT_URL: IMA_SDK_DEFAULT_URL,
  resolveModernVideoAdsFlags: resolveModernVideoAdsFlags,
  isModernVideoAdsEnabled: isModernVideoAdsEnabled,
  isLiveImaAdsEnabled: isLiveImaAdsEnabled,
};
